import { IS_DEBUG_BUILD, numericCompare } from "Util/util";
import { PROJECT_NAMESPACE, PROJECT_TYPES } from "SharedStores/Project/projectStore";
import { ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";
import { isModuleRegistered } from "SharedStores/util";
import { isEnvelopeStatusTerminal, isEnvelopeStatusFailed } from "ESignatures/eSignatureConstants";
import { ENVELOPE_STATUS } from "ESignatures/eSignatureConstants";
import { mapI18n } from "Util/i18n";
import { KEYS, NAMESPACE } from "../i18n/eSignature.i18n";
import Vuex from "vuex";

// List of property names which must be present on whatever we're mixing this
// into in order for the computeds to return accurate results
const expectedPropertyNames = ["$store", "envelope", "envelopeDefinition"];

export const envelopeElementMixin = {
    // Verify expected properties present on mixee
    mounted() {
        for (const propertyName of expectedPropertyNames) {
            checkForMissingPropertyOnMixee(this, propertyName);
        }
    },
    computed: {
        ...mapI18n(KEYS, NAMESPACE),
        envelopeElement_hasEnvelope() {
            return Boolean(this.envelope);
        },
        envelopeElement_orderedItemDefinitions() {
            if (!this.envelopeDefinition) {
                return [];
            }

            const items = Object.values(this.envelopeDefinition.Items);
            return items.sort((a, b) => numericCompare(a.Order, b.Order));
        },
        envelopeElement_orderedItems() {
            if (!this.envelopeElement_hasEnvelope) {
                return [];
            }
            const items = Object.values(this.envelope.Items);
            return items.sort((a, b) => {
                const aDefinition = this.envelopeDefinition.Items[a.DefinitionId];
                const bDefinition = this.envelopeDefinition.Items[b.DefinitionId];
                return numericCompare(aDefinition.Order, bDefinition.Order);
            });
        },
        envelopeElement_itemsByDefinitionId() {
            if (!this.envelopeElement_hasEnvelope) {
                return {};
            }
            const itemsByDefinitionId = {};
            this.envelopeElement_orderedItems.forEach((item) => {
                itemsByDefinitionId[item.DefinitionId] = item;
            });
            return itemsByDefinitionId;
        },
        envelopeElement_isProjectModuleRegistered() {
            return isModuleRegistered(PROJECT_NAMESPACE, this.$store.state);
        },
        envelopeElement_isStatusTerminal() {
            if (!this.envelopeElement_hasEnvelope) {
                return false;
            }
            return isEnvelopeStatusTerminal(this.envelope.Status.Code);
        },
        envelopeElement_isStatusFailed() {
            if (!this.envelopeElement_hasEnvelope) {
                return false;
            }
            return isEnvelopeStatusFailed(this.envelope.Status.Code);
        },
        envelopeElement_isStatusCompleted() {
            if (!this.envelopeElement_hasEnvelope) {
                return false;
            }
            return this.envelope.Status.Code === ENVELOPE_STATUS.Completed;
        },
        envelopeElement_isStatusNew() {
            if (!this.envelopeElement_hasEnvelope) {
                return false;
            }
            return this.envelope.Status.Code === ENVELOPE_STATUS.New;
        },
        envelopeElement_showTargetAttachmentLink() {
            return this.envelopeElement_isStatusCompleted && this.envelope.TargetAttachmentId;
        },
        envelopeElement_recipientDefinitions() {
            return Object.values(this.envelopeDefinition.Recipients);
        },
        envelopeElement_recipients() {
            return Object.values(this.envelope.Recipients);
        },
        envelopeElement_recipientsByDefinitionId() {
            if (!this.envelopeElement_hasEnvelope) {
                return {};
            }
            const recipientsByDefinitionId = {};
            this.envelopeElement_recipients.forEach((recipient) => {
                recipientsByDefinitionId[recipient.DefinitionId] = recipient;
            });
            return recipientsByDefinitionId;
        },
        envelopeElement_missingRequiredAttachment() {
            if (!this.envelopeElement_isProjectModuleRegistered) {
                return false;
            }
            for (const itemDefinition of this.envelopeElement_orderedItemDefinitions) {
                if (itemDefinition.IsRequired && this.envelopeElement_missingAttachment(itemDefinition)) {
                    return true;
                }
            }
            return false;
        },
        envelopeElement_recipientContactInfoComplete() {
            if (!this.envelopeElement_isProjectModuleRegistered) {
                return true;
            }
            for (const recipientDefinition of this.envelopeElement_recipientDefinitions) {
                if (!isRecipientContactInfoComplete(this, recipientDefinition)) {
                    return false;
                }
            }
            return true;
        },
        ...Vuex.mapGetters({
            attachmentFromId: PROJECT_TYPES.getters.attachmentFromId,
            dataFieldValueFromId: PROJECT_TYPES.getters.dataFieldValueFromId
        })
    },
    methods: {
        envelopeElement_missingAttachment(itemDefinition) {
            return (
                this.envelopeElement_isProjectModuleRegistered &&
                itemDefinition?.AttachmentDefinitionId &&
                !this.attachmentFromId(itemDefinition.AttachmentDefinitionId)
            );
        },
        envelopeElement_itemIsPresentByItemDefinitionId(itemDefinitionId) {
            const item = this.envelopeElement_itemsByDefinitionId[itemDefinitionId];
            return Boolean(item);
        }
    }
};

function checkForMissingPropertyOnMixee(mixee, propertyName) {
    // Can't use "hasOwnProperty" since the property may be inherited
    if (IS_DEBUG_BUILD && !(propertyName in mixee)) {
        throw `Missing expected property "${propertyName}"`;
    }
}
function isRecipientContactInfoComplete(self, recipientDefinition) {
    switch (recipientDefinition.ContactInfoType) {
        case ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.Fixed:
            return true;
        case ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields:
            return (
                self.dataFieldValueFromId(recipientDefinition.FirstNameFieldId) &&
                self.dataFieldValueFromId(recipientDefinition.LastNameFieldId) &&
                self.dataFieldValueFromId(recipientDefinition.EmailFieldId)
            );
        case ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField:
            return self.dataFieldValueFromId(recipientDefinition.UserFieldId);
        default:
            throw "Unrecognized contact info type";
    }
}
